import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bot, FileText, Upload, Zap, Database, ArrowRight, CheckCircle, Settings, Globe, Rocket, Brain } from 'lucide-react';

const TransformationRobotProject = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const v1Features = [
    { icon: Upload, title: 'Excel File Selection', desc: 'Browse and select Excel files automatically copied to input directory' },
    { icon: Settings, title: 'Editable Configuration', desc: 'Column mapping, field mapping, and code tables configuration' },
    { icon: FileText, title: 'Real-time Logs', desc: 'Detailed processing information and conversion logs' },
    { icon: Brain, title: 'LLM Code Mapping', desc: 'Intelligent code recognition and mapping powered by LLM' },
    { icon: Globe, title: 'Limo API Integration', desc: 'One-click submit to Limo system to create products' }
  ];

  const v2Features = [
    { icon: Globe, title: 'Web-Based Service', desc: 'FastAPI backend with RESTful API endpoints' },
    { icon: Upload, title: 'Excel Upload', desc: 'Direct file upload through web interface' },
    { icon: Database, title: 'Database Integration', desc: 'Peewee ORM for data persistence' },
    { icon: Settings, title: 'Configuration Management', desc: 'Centralized configuration system' },
    { icon: Zap, title: 'Async Processing', desc: 'Asyncio for high-performance processing' }
  ];

  const workflow = [
    {
      step: '01',
      title: 'Upload Product Memo',
      icon: Upload,
      desc: 'BA uploads Excel file containing product defined names',
      details: ['Excel file with defined names', 'Product configuration data', 'Business rules and mappings']
    },
    {
      step: '02',
      title: 'Intelligent Conversion',
      icon: Zap,
      desc: 'System converts Excel to structured JSON with code mapping',
      details: ['Column mapping application', 'Field transformation', 'LLM-powered code recognition', 'Code table lookups']
    },
    {
      step: '03',
      title: 'Preview & Validate',
      icon: FileText,
      desc: 'Review converted JSON with real-time validation',
      details: ['JSON preview interface', 'Validation checks', 'Error highlighting', 'Correction suggestions']
    },
    {
      step: '04',
      title: 'Submit to Limo',
      icon: Rocket,
      desc: 'One-click API call to create product in Limo system',
      details: ['RESTful API integration', 'Automatic product creation', 'Confirmation feedback', 'Error handling']
    }
  ];

  const businessImpact = [
    {
      value: '90%',
      label: 'Time Reduction',
      desc: 'Manual configuration to automated conversion',
      color: 'from-green-500 to-emerald-500'
    },
    {
      value: '100%',
      label: 'Accuracy Rate',
      desc: 'LLM-powered intelligent code mapping',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      value: '10x',
      label: 'Efficiency Boost',
      desc: 'Products configured per day per BA',
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/30 p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-lg">
          <Bot className="w-8 h-8 text-white" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-purple-400">Product Transformation Robot</h3>
          <p className="text-sm text-gray-400">Excel to JSON Converter for Limo Product Configuration</p>
        </div>
      </div>

      {/* Introduction */}
      <div className="mb-6 p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
        <p className="text-gray-300 leading-relaxed">
          An intelligent automation tool that converts <span className="text-purple-400 font-bold">Product Memo (Excel)</span> into
          <span className="text-pink-400 font-bold"> Limo product configuration JSON</span>, dramatically reducing manual configuration work for Business Analysts.
        </p>
      </div>

      {/* Version Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* V1 - App Version */}
        <div className="bg-white/5 rounded-xl border border-purple-500/30 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500/20 to-purple-500/10 p-4 border-b border-purple-500/20">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500 p-2 rounded-lg">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">V1 - Desktop App</h4>
                <p className="text-xs text-gray-400">Local Executable Application</p>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-3 mb-4">
              {v1Features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-2">
                    <Icon className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-semibold text-white">{feature.title}</h5>
                      <p className="text-xs text-gray-400">{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* V1 Screenshot */}
            <div className="relative aspect-video bg-gray-900/50 rounded-lg overflow-hidden cursor-pointer mt-4"
                 onClick={() => setSelectedImage({ src: '/pic/tpr/app.webp', title: 'MemoToJson Desktop App v1' })}>
              <img
                src="/pic/tpr/app.webp"
                alt="Desktop App v1"
                className="w-full h-full object-cover object-left-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity">
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <p className="text-white text-sm font-semibold">Click to enlarge</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* V2 - Web Version */}
        <div className="bg-white/5 rounded-xl border border-pink-500/30 overflow-hidden">
          <div className="bg-gradient-to-r from-pink-500/20 to-pink-500/10 p-4 border-b border-pink-500/20">
            <div className="flex items-center gap-3">
              <div className="bg-pink-500 p-2 rounded-lg">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-white">V2 - Web Service</h4>
                <p className="text-xs text-gray-400">Multi-user Web Platform</p>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-3 mb-4">
              {v2Features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start gap-2">
                    <Icon className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h5 className="text-xs font-semibold text-white">{feature.title}</h5>
                      <p className="text-xs text-gray-400">{feature.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* V2 Screenshot */}
            <div className="relative aspect-video bg-gray-900/50 rounded-lg overflow-hidden cursor-pointer mt-4"
                 onClick={() => setSelectedImage({ src: '/pic/tpr/web.webp', title: 'Web Service v2' })}>
              <img
                src="/pic/tpr/web.webp"
                alt="Web Service v2"
                className="w-full h-full object-cover object-left-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity">
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <p className="text-white text-sm font-semibold">Click to enlarge</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow */}
      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/30 mb-6">
        <h4 className="text-lg font-bold text-purple-400 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Automated Workflow
        </h4>

        <div className="space-y-4">
          {workflow.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.step} className="relative">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                      {step.step}
                    </div>
                  </div>
                  <div className="flex-1 bg-white/5 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="bg-purple-500/20 p-2 rounded-lg">
                        <Icon className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h5 className="font-bold text-white mb-1">{step.title}</h5>
                        <p className="text-xs text-gray-400">{step.desc}</p>
                      </div>
                    </div>
                    <ul className="ml-10 space-y-1">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Arrow */}
                {index < workflow.length - 1 && (
                  <div className="flex justify-center py-2">
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Business Impact */}
      <div className="bg-white/5 rounded-xl p-6 border border-pink-500/30">
        <h4 className="text-lg font-bold text-pink-400 mb-4 flex items-center gap-2">
          <Rocket className="w-5 h-5" />
          Business Impact
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {businessImpact.map((impact, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gradient-to-br ${impact.color} rounded-lg p-4 relative overflow-hidden`}
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative">
                <div className="text-3xl font-bold text-white mb-1">{impact.value}</div>
                <div className="text-sm font-semibold text-white/90 mb-1">{impact.label}</div>
                <p className="text-xs text-white/80">{impact.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedImage(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative max-w-6xl max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white hover:text-purple-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <span className="text-2xl">✕</span>
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-center text-white mt-4">{selectedImage.title}</p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default TransformationRobotProject;
